/**
 * DocResults — Search results list with highlighted snippets.
 */
import { createElement } from 'react';
import type { DocSearchResult } from '../../types';

interface DocResultsProps {
  results: DocSearchResult[];
  loading: boolean;
  onSelect: (result: DocSearchResult) => void;
}

export function DocResults({ results, loading, onSelect }: DocResultsProps) {
  if (loading) {
    return createElement(
      'div',
      {
        style: {
          padding: '24px 16px',
          textAlign: 'center' as const,
          color: 'var(--sw-text-secondary)',
          fontSize: '14px',
        },
        role: 'status',
        'aria-live': 'polite',
      },
      'Searching...',
    );
  }

  if (results.length === 0) {
    return createElement(
      'div',
      {
        style: {
          padding: '24px 16px',
          textAlign: 'center' as const,
          color: 'var(--sw-text-secondary)',
          fontSize: '14px',
        },
        role: 'status',
        'aria-live': 'polite',
      },
      'No results found. Try different keywords.',
    );
  }

  return createElement(
    'div',
    {
      style: { padding: '8px 0' },
      role: 'list',
      'aria-label': `${results.length} search results`,
    },
    ...results.map((result) =>
      createElement(
        'button',
        {
          key: result.sectionId,
          type: 'button',
          role: 'listitem',
          onClick: () => onSelect(result),
          'aria-label': `View: ${result.title}`,
          style: {
            display: 'block',
            width: '100%',
            textAlign: 'left' as const,
            padding: '12px',
            marginBottom: '4px',
            backgroundColor: 'var(--sw-bg-surface)',
            border: '1px solid var(--sw-border)',
            borderRadius: 'var(--sw-border-radius)',
            cursor: 'pointer',
            color: 'var(--sw-text-primary)',
            fontFamily: 'var(--sw-font-family)',
            fontSize: '14px',
            lineHeight: 1.4,
            minHeight: '48px',
            boxSizing: 'border-box' as const,
          },
        },
        // Title row with optional score badge
        createElement(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '4px',
            },
          },
          createElement(
            'span',
            { style: { fontWeight: 600 } },
            result.title,
          ),
          result.score != null && result.score > 0
            ? createElement(
                'span',
                {
                  style: {
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    backgroundColor: 'var(--sw-accent-primary)',
                    color: '#000',
                    fontWeight: 600,
                    flexShrink: 0,
                    marginLeft: '8px',
                  },
                },
                `${Math.round(result.score * 100)}%`,
              )
            : null,
        ),
        // Snippet
        createElement(
          'div',
          {
            style: {
              fontSize: '13px',
              color: 'var(--sw-text-secondary)',
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical' as const,
            },
          },
          result.snippet,
        ),
      ),
    ),
  );
}
